import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStorefront } from './StoreContext';

/**
 * Built-in slug palettes for visitor fallback only
 * (no published store theme and no catalog/editor preview URL).
 * CSS derives the full scale from --hue / --theme-sat in stylesheet.css.
 */
export const themes = {
    classic: { '--hue': '47', '--theme-sat': '35%' },
    modern: { '--hue': '215', '--theme-sat': '65%' },
    midnight: { '--hue': '222', '--theme-sat': '40%' },
    forest: { '--hue': '158', '--theme-sat': '45%' },
    rose: { '--hue': '345', '--theme-sat': '55%' },
    slate: { '--hue': '210', '--theme-sat': '15%' },
    emerald: { '--hue': '150', '--theme-sat': '65%' },
    copper: { '--hue': '25', '--theme-sat': '50%' },
    lavender: { '--hue': '265', '--theme-sat': '45%' },
    sage: { '--hue': '120', '--theme-sat': '20%' },
    ocean: { '--hue': '200', '--theme-sat': '70%' },
    amber: { '--hue': '38', '--theme-sat': '85%' },
    coral: { '--hue': '12', '--theme-sat': '70%' },
    mint: { '--hue': '165', '--theme-sat': '40%' },
    sand: { '--hue': '35', '--theme-sat': '25%' },
    onyx: { '--hue': '220', '--theme-sat': '12%' },
    amethyst: { '--hue': '280', '--theme-sat': '55%' },
    sunset: { '--hue': '18', '--theme-sat': '80%' },
    teal: { '--hue': '185', '--theme-sat': '60%' },
    plum: { '--hue': '310', '--theme-sat': '45%' },
    steel: { '--hue': '215', '--theme-sat': '25%' },
    olive: { '--hue': '80', '--theme-sat': '35%' },
    ruby: { '--hue': '355', '--theme-sat': '65%' },
    sky: { '--hue': '195', '--theme-sat': '75%' },
    mocha: { '--hue': '28', '--theme-sat': '35%' },
    // Catalog seed slugs used by platform / merchant Online Store
    default: { '--hue': '210', '--theme-sat': '60%' },
    retro: { '--hue': '346', '--theme-sat': '80%' },
};

const DEFAULT_SLUG = 'classic';
const LOCAL_STORAGE_KEY = 'zilvix-storefront-theme';
const SESSION_STORAGE_PREVIEW_KEY = 'zilvix-theme-preview';

/** Per browsing-context sticky preview (each iframe has its own JS realm). */
let frameStickyPreview = null;

function isTopLevelWindow() {
    if (typeof window === 'undefined') return false;
    try {
        return window.self === window.top;
    } catch {
        // Cross-origin frame access can throw — treat as embedded.
        return false;
    }
}

/** Older ThemeContext set full palettes inline; clear so stylesheet.css takes over. */
const CLEARED_THEME_VARS = [
    '--first-color',
    '--first-color-25',
    '--first-color-50',
    '--first-color-100',
    '--first-color-150',
    '--first-color-200',
    '--first-color-250',
    '--first-color-300',
    '--first-color-350',
    '--first-color-400',
    '--first-color-450',
    '--first-color-500',
    '--first-color-550',
    '--first-color-600',
    '--first-color-650',
    '--first-color-700',
    '--first-color-750',
    '--first-color-800',
    '--first-color-850',
    '--first-color-900',
    '--title-color',
    '--text-color',
    '--text-color-light',
    '--body-color',
    '--container-color',
    '--text-on-primary',
    '--swatch-color',
];

const ThemeContext = createContext(null);

function resolveThemeSlug(candidates) {
    for (const candidate of candidates) {
        const slug = String(candidate || '')
            .trim()
            .toLowerCase();
        if (slug && themes[slug]) return slug;
    }
    return DEFAULT_SLUG;
}

function normalizeSat(value, fallback = '35%') {
    const raw = String(value ?? '').trim();
    if (!raw) return fallback;
    const match = raw.match(/^(\d+(?:\.\d+)?)\s*%?$/);
    if (!match) return fallback;
    const n = Math.min(100, Math.max(0, Number(match[1])));
    if (!Number.isFinite(n)) return fallback;
    return `${Math.round(n)}%`;
}

function normalizeHue(value, fallback = '47') {
    const n = Number.parseInt(String(value ?? '').trim(), 10);
    if (!Number.isFinite(n)) return fallback;
    return String(((n % 360) + 360) % 360);
}

function parsePreview(search) {
    const params = new URLSearchParams(search || '');

    if (params.get('clearPreview') === '1') {
        frameStickyPreview = null;
        if (isTopLevelWindow()) {
            try {
                window.sessionStorage.removeItem(SESSION_STORAGE_PREVIEW_KEY);
            } catch {
                /* ignore */
            }
        }
        return {
            slug: '',
            hue: '',
            themeSat: '',
            hasKnobs: false,
            forceCatalogPreview: false,
            isPreview: false,
        };
    }

    const themeSlug = String(params.get('themeId') || params.get('theme') || '')
        .trim()
        .toLowerCase();
    const hueParam = params.get('hue');
    const satParam = params.get('themeSat') || params.get('sat');
    const forceCatalogPreview =
        params.get('catalogPreview') === '1' || params.get('preview') === '1';

    const hasHue = hueParam != null && String(hueParam).trim() !== '';
    const hasSat = satParam != null && String(satParam).trim() !== '';
    const isUrlPreview = forceCatalogPreview || Boolean(themeSlug) || hasHue || hasSat;

    if (isUrlPreview) {
        const state = {
            slug: themeSlug,
            hue: hasHue ? normalizeHue(hueParam) : '',
            themeSat: hasSat ? normalizeSat(satParam) : '',
            hasKnobs: hasHue || hasSat,
            forceCatalogPreview,
            isPreview: true,
        };
        frameStickyPreview = state;
        // Persist only for top-level preview navigation — never for dashboard embeds,
        // or live SiteEmbedPreview inherits the last catalog card's hue.
        if (isTopLevelWindow()) {
            try {
                window.sessionStorage.setItem(SESSION_STORAGE_PREVIEW_KEY, JSON.stringify(state));
            } catch {
                /* ignore */
            }
        }
        return state;
    }

    // Same iframe/document SPA hop: keep knobs without sharing across sibling embeds.
    if (frameStickyPreview?.isPreview) {
        return frameStickyPreview;
    }

    // Top-level tab only: restore after soft navigations / reloads of a preview link.
    if (isTopLevelWindow()) {
        try {
            const saved = window.sessionStorage.getItem(SESSION_STORAGE_PREVIEW_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === 'object' && parsed.isPreview) {
                    frameStickyPreview = parsed;
                    return parsed;
                }
            }
        } catch {
            /* ignore */
        }
    }

    return {
        slug: '',
        hue: '',
        themeSat: '',
        hasKnobs: false,
        forceCatalogPreview: false,
        isPreview: false,
    };
}

function knobsForSlug(slug) {
    const base = themes[slug] || themes[DEFAULT_SLUG];
    return {
        hue: base['--hue'],
        themeSat: base['--theme-sat'],
    };
}

function resolveKnobs(defaults, settings, slugFallback) {
    const base = slugFallback || knobsForSlug(DEFAULT_SLUG);
    return {
        hue: normalizeHue(settings?.hue ?? defaults?.hue ?? base.hue, base.hue),
        themeSat: normalizeSat(
            settings?.themeSat ?? defaults?.themeSat ?? base.themeSat,
            base.themeSat,
        ),
    };
}

function readSavedSlug() {
    try {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        return saved && themes[saved] ? saved : DEFAULT_SLUG;
    } catch {
        return DEFAULT_SLUG;
    }
}

function writeSavedSlug(slug) {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, slug);
    } catch {
        /* private mode / blocked storage */
    }
}

/** Apply --hue / --theme-sat only; stylesheet.css derives the rest. */
function applyHueSat(hue, themeSat) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    CLEARED_THEME_VARS.forEach((property) => {
        root.style.removeProperty(property);
    });

    root.style.setProperty('--hue', normalizeHue(hue, knobsForSlug(DEFAULT_SLUG).hue));
    root.style.setProperty(
        '--theme-sat',
        normalizeSat(themeSat, knobsForSlug(DEFAULT_SLUG).themeSat),
    );
}

export const ThemeProvider = ({ children }) => {
    const { store } = useStorefront();
    const { search } = useLocation();

    const preview = useMemo(() => parsePreview(search), [search]);
    const storeTheme = store?.theme || null;
    const storeSlug = storeTheme?.themeSlug || '';
    const storeSettings = storeTheme?.settings || null;
    const storeDefaults = storeTheme?.defaults || null;

    // Preview URL wins over published theme and over localStorage.
    const themeSource = preview.isPreview ? 'preview' : storeTheme ? 'store' : 'fallback';
    const canSwitch = themeSource === 'fallback';

    const [manualSlug, setManualSlug] = useState(() => {
        if (typeof window !== 'undefined' && parsePreview(window.location.search).isPreview) {
            return DEFAULT_SLUG;
        }
        return readSavedSlug();
    });

    const currentTheme = useMemo(() => {
        if (themeSource === 'preview') {
            return preview.slug || DEFAULT_SLUG;
        }
        if (themeSource === 'store') {
            return storeSlug || DEFAULT_SLUG;
        }
        return resolveThemeSlug([manualSlug, DEFAULT_SLUG]);
    }, [themeSource, preview.slug, storeSlug, manualSlug]);

    const slugFallback = useMemo(() => {
        if (themeSource === 'preview' && preview.slug) {
            return knobsForSlug(preview.slug);
        }
        if (themeSource === 'store' && storeSlug) {
            return knobsForSlug(storeSlug);
        }
        return knobsForSlug(themes[currentTheme] ? currentTheme : DEFAULT_SLUG);
    }, [themeSource, preview.slug, storeSlug, currentTheme]);

    const activeKnobs = useMemo(() => {
        if (themeSource === 'preview') {
            return {
                hue: normalizeHue(preview.hue || slugFallback.hue, slugFallback.hue),
                themeSat: normalizeSat(
                    preview.themeSat || slugFallback.themeSat,
                    slugFallback.themeSat,
                ),
            };
        }
        if (themeSource === 'store') {
            return resolveKnobs(storeDefaults, storeSettings, slugFallback);
        }
        return slugFallback;
    }, [
        themeSource,
        preview.hue,
        preview.themeSat,
        storeDefaults,
        storeSettings,
        slugFallback,
    ]);

    useEffect(() => {
        applyHueSat(activeKnobs.hue, activeKnobs.themeSat);
        if (themeSource === 'fallback') {
            writeSavedSlug(currentTheme);
        }
    }, [activeKnobs.hue, activeKnobs.themeSat, themeSource, currentTheme]);

    const switchTheme = (themeName) => {
        if (!themes[themeName] || !canSwitch) return;
        setManualSlug(themeName);
        writeSavedSlug(themeName);
    };

    const value = {
        currentTheme,
        switchTheme,
        availableThemes: Object.keys(themes),
        themes,
        themeSource,
        canSwitch,
        storeTheme,
        defaults: storeDefaults,
        settings: storeSettings,
        hue: activeKnobs.hue,
        themeSat: activeKnobs.themeSat,
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used inside ThemeProvider');
    }
    return ctx;
};
